import { create } from 'zustand';
import { ApiError, apiFetch } from '../../../lib/api';
import { resolveSectorProfile } from '@shared/constants/naf-usages';
import type {
  ComparateurDossierPublic,
  DossierContactInput,
  DossierEnergyInput,
  DossierUsagesInput,
  DossierBillAck,
  DossierUploadUrlRequest,
  DossierBillInsightsInput,
} from '@shared/schemas/comparateur';

export type FunnelStep =
  | 'lookup'
  | 'contact'
  | 'energy'
  | 'usages'
  | 'bills'
  | 'review'
  | 'thank_you';

// Ordre aligné sur le flux réel : lookup → contact → energy → usages? → bills → review
export const FUNNEL_STEPS: readonly FunnelStep[] = [
  'lookup',
  'contact',
  'energy',
  'usages',
  'bills',
  'review',
  'thank_you',
] as const;

export const STEP_LABELS: Record<FunnelStep, string> = {
  lookup: 'Entreprise',
  contact: 'Contact',
  energy: 'Énergie',
  usages: 'Usages',
  bills: 'Factures',
  review: 'Validation',
  thank_you: 'Confirmation',
};

/**
 * Détermine si l'étape Usages doit être proposée pour ce dossier.
 * On la propose dès qu'un profil sectoriel est résolvable depuis le code NAF.
 */
export function hasUsagesStep(d: ComparateurDossierPublic | null): boolean {
  if (!d?.company?.nafCode) return false;
  return Boolean(resolveSectorProfile(d.company.nafCode));
}

/**
 * Renvoie les étapes visibles dans la barre de progression pour ce dossier.
 * Filtre `usages` si aucun profil sectoriel n'est applicable.
 */
export function getVisibleSteps(d: ComparateurDossierPublic | null): FunnelStep[] {
  return FUNNEL_STEPS.filter((step) => {
    if (step === 'thank_you') return false;
    if (step === 'usages' && !hasUsagesStep(d)) return false;
    return true;
  });
}

/**
 * Position 1-indexée de l'étape courante dans la barre, et total visible.
 * Ex: { current: 4, total: 6 } pour l'étape `usages` quand un profil est détecté.
 */
export function getStepPosition(
  step: FunnelStep,
  d: ComparateurDossierPublic | null,
): { current: number; total: number } {
  const visible = getVisibleSteps(d);
  const idx = visible.indexOf(step);
  return { current: idx >= 0 ? idx + 1 : visible.length, total: visible.length };
}

interface UploadResponse {
  upload: {
    method: 'PUT';
    url: string;
    key: string;
    headers: Record<string, string>;
    expiresInSeconds: number;
  };
  dossier: ComparateurDossierPublic;
}

interface ComparateurState {
  step: FunnelStep;
  /** Pile des étapes précédentes — permet un "Retour" fidèle au chemin parcouru. */
  stepHistory: FunnelStep[];
  dossier: ComparateurDossierPublic | null;
  loading: boolean;
  error: string | null;
  setError: (msg: string | null) => void;
  /** Navigue vers une étape et empile l'étape courante dans l'historique. */
  setStep: (step: FunnelStep) => void;
  /** Revient à l'étape précédente dans la pile (no-op si pile vide). */
  goBack: () => void;

  initFromDossier: (dossier: ComparateurDossierPublic) => void;
  startNewDossier: (utm?: Record<string, string | undefined>) => Promise<void>;
  selectCompany: (siret: string) => Promise<void>;
  saveContact: (input: DossierContactInput) => Promise<void>;
  saveEnergy: (input: DossierEnergyInput) => Promise<void>;
  saveUsages: (input: DossierUsagesInput) => Promise<void>;
  skipUsages: () => void;
  uploadBill: (season: 'summer' | 'winter', file: File) => Promise<void>;
  saveBillInsights: (input: DossierBillInsightsInput) => Promise<void>;
  requestRemindLater: () => Promise<void>;
  submit: (input: {
    consentRgpd: boolean;
    consentMarketing: boolean;
    message?: string;
  }) => Promise<string | undefined>;
  reset: () => void;
}

function detectStepFromDossier(d: ComparateurDossierPublic): FunnelStep {
  if (d.status === 'submitted' || d.status === 'converted') return 'thank_you';
  if (!d.company) return 'lookup';
  if (!d.contact || !d.contact.email) return 'contact';
  if (!d.energyChoice) return 'energy';
  if (hasUsagesStep(d) && !d.usages) return 'usages';
  if (!d.bills.summer && !d.bills.winter && !d.bills.requested) return 'bills';
  return 'review';
}

function readErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Une erreur est survenue, merci de réessayer.';
}

const RESUME_TOKEN_KEY = 'sef_comparateur_resume_token';

function persistResumeToken(token: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    if (token) sessionStorage.setItem(RESUME_TOKEN_KEY, token);
    else sessionStorage.removeItem(RESUME_TOKEN_KEY);
  } catch {
    /* ignore quota / private mode */
  }
}

function readPersistedResumeToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(RESUME_TOKEN_KEY);
  } catch {
    return null;
  }
}

export const useComparateurStore = create<ComparateurState>((set, get) => ({
  step: 'lookup',
  stepHistory: [],
  dossier: null,
  loading: false,
  error: null,

  setError: (msg) => set({ error: msg }),
  setStep: (step) => set((s) => ({ step, error: null, stepHistory: [...s.stepHistory, s.step] })),
  goBack: () =>
    set((s) => {
      const prev = s.stepHistory.at(-1);
      if (!prev) return s;
      return { step: prev, error: null, stepHistory: s.stepHistory.slice(0, -1) };
    }),

  initFromDossier: (dossier) => {
    persistResumeToken(dossier.resumeToken);
    set({ dossier, step: detectStepFromDossier(dossier), stepHistory: [], error: null });
  },

  startNewDossier: async (utm) => {
    // Guard contre le double-mount React StrictMode : on n'initie un nouveau dossier
    // que si aucun n'est en cours de chargement et qu'aucun n'existe déjà.
    if (get().loading || get().dossier) return;
    set({ loading: true, error: null, stepHistory: [] });
    try {
      const savedToken = readPersistedResumeToken();
      if (savedToken) {
        try {
          const { dossier } = await apiFetch<{ dossier: ComparateurDossierPublic }>(
            `/api/comparateur/dossier/${encodeURIComponent(savedToken)}`,
          );
          if (dossier.status !== 'submitted' && dossier.status !== 'converted') {
            persistResumeToken(dossier.resumeToken);
            set({
              dossier,
              step: detectStepFromDossier(dossier),
              stepHistory: [],
              loading: false,
            });
            return;
          }
          persistResumeToken(null);
        } catch {
          persistResumeToken(null);
        }
      }

      const sessionId =
        typeof window !== 'undefined'
          ? (sessionStorage.getItem('sef_session_id') ?? undefined)
          : undefined;
      const { dossier } = await apiFetch<{ dossier: ComparateurDossierPublic }>(
        '/api/comparateur/dossier',
        {
          json: {
            source: 'comparateur',
            referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
            sessionId,
            utm,
          },
        },
      );
      persistResumeToken(dossier.resumeToken);
      set({ dossier, step: detectStepFromDossier(dossier), stepHistory: [], loading: false });
    } catch (err) {
      set({ error: readErrorMessage(err), loading: false });
      throw err;
    }
  },

  selectCompany: async (siret) => {
    const token = get().dossier?.resumeToken;
    if (!token) throw new Error('Dossier non initialisé');
    set({ loading: true, error: null });
    try {
      const { dossier } = await apiFetch<{ dossier: ComparateurDossierPublic }>(
        `/api/comparateur/dossier/${encodeURIComponent(token)}/company`,
        { method: 'PATCH', json: { siret } },
      );
      set({ dossier, step: 'contact', loading: false });
    } catch (err) {
      set({ error: readErrorMessage(err), loading: false });
      throw err;
    }
  },

  saveContact: async (input) => {
    const token = get().dossier?.resumeToken;
    if (!token) throw new Error('Dossier non initialisé');
    set({ loading: true, error: null });
    try {
      const { dossier } = await apiFetch<{ dossier: ComparateurDossierPublic }>(
        `/api/comparateur/dossier/${encodeURIComponent(token)}/contact`,
        { method: 'PATCH', json: input },
      );
      set({ dossier, step: 'energy', loading: false });
    } catch (err) {
      set({ error: readErrorMessage(err), loading: false });
      throw err;
    }
  },

  saveEnergy: async (input) => {
    const token = get().dossier?.resumeToken;
    if (!token) throw new Error('Dossier non initialisé');
    set({ loading: true, error: null });
    try {
      const { dossier } = await apiFetch<{ dossier: ComparateurDossierPublic }>(
        `/api/comparateur/dossier/${encodeURIComponent(token)}/energy`,
        { method: 'PATCH', json: input },
      );
      const nextStep: FunnelStep = hasUsagesStep(dossier) ? 'usages' : 'bills';
      set({ dossier, step: nextStep, loading: false });
    } catch (err) {
      set({ error: readErrorMessage(err), loading: false });
      throw err;
    }
  },

  saveUsages: async (input) => {
    const token = get().dossier?.resumeToken;
    if (!token) throw new Error('Dossier non initialisé');
    set({ loading: true, error: null });
    try {
      const { dossier } = await apiFetch<{ dossier: ComparateurDossierPublic }>(
        `/api/comparateur/dossier/${encodeURIComponent(token)}/usages`,
        { method: 'PATCH', json: input },
      );
      set({ dossier, step: 'bills', loading: false });
    } catch (err) {
      set({ error: readErrorMessage(err), loading: false });
      throw err;
    }
  },

  skipUsages: () => {
    set({ step: 'bills', error: null });
  },

  uploadBill: async (season, file) => {
    const token = get().dossier?.resumeToken;
    if (!token) throw new Error('Dossier non initialisé');

    set({ loading: true, error: null });
    try {
      const payload: DossierUploadUrlRequest = {
        season,
        filename: file.name,
        contentType: file.type as DossierUploadUrlRequest['contentType'],
        size: file.size,
      };

      const presignedResp = await apiFetch<UploadResponse>(
        `/api/comparateur/dossier/${encodeURIComponent(token)}/upload-url`,
        { json: payload },
      );

      if (!presignedResp.upload.url.startsWith('mock://')) {
        const putResp = await fetch(presignedResp.upload.url, {
          method: 'PUT',
          headers: presignedResp.upload.headers,
          body: file,
        });
        if (!putResp.ok) {
          throw new Error(
            `Upload échoué (HTTP ${putResp.status}). Réessayez ou utilisez un autre format.`,
          );
        }
      }

      const ack: DossierBillAck = {
        season,
        key: presignedResp.upload.key,
        filename: file.name,
        contentType: file.type,
        size: file.size,
      };
      const { dossier } = await apiFetch<{ dossier: ComparateurDossierPublic }>(
        `/api/comparateur/dossier/${encodeURIComponent(token)}/bill`,
        { method: 'PATCH', json: ack },
      );
      set({ dossier });
    } catch (err) {
      set({ error: readErrorMessage(err) });
      throw err;
    } finally {
      set({ loading: false });
    }
  },

  saveBillInsights: async (input) => {
    const token = get().dossier?.resumeToken;
    if (!token) return;
    try {
      const { dossier } = await apiFetch<{ dossier: ComparateurDossierPublic }>(
        `/api/comparateur/dossier/${encodeURIComponent(token)}/bill-insights`,
        { method: 'PATCH', json: input },
      );
      set({ dossier });
    } catch {
      // Non bloquant — échec silencieux, l'upload reste valide
    }
  },

  requestRemindLater: async () => {
    const token = get().dossier?.resumeToken;
    if (!token) throw new Error('Dossier non initialisé');
    set({ loading: true, error: null });
    try {
      const { dossier } = await apiFetch<{ dossier: ComparateurDossierPublic }>(
        `/api/comparateur/dossier/${encodeURIComponent(token)}/remind-later`,
        { json: { reason: 'bills_not_available', consentEmail: true } },
      );
      set({ dossier, step: 'review', loading: false });
    } catch (err) {
      set({ error: readErrorMessage(err), loading: false });
      throw err;
    }
  },

  submit: async (input) => {
    const token = get().dossier?.resumeToken;
    if (!token) throw new Error('Dossier non initialisé');
    set({ loading: true, error: null });
    try {
      const { dossier, leadId } = await apiFetch<{
        dossier: ComparateurDossierPublic;
        leadId: string;
      }>(`/api/comparateur/dossier/${encodeURIComponent(token)}/submit`, { json: input });
      set({ dossier, step: 'thank_you', loading: false });
      persistResumeToken(null);
      return leadId;
    } catch (err) {
      set({ error: readErrorMessage(err), loading: false });
      throw err;
    }
  },

  reset: () => {
    persistResumeToken(null);
    set({ dossier: null, step: 'lookup', error: null, loading: false, stepHistory: [] });
  },
}));
